import React from "react";
import { Typography, Box, Container, useTheme, Paper, Stack } from "@mui/material";

const milestones = [
  {
    title: "Our Founding",
    year: "2023",
    description:
      "Launched in the UK with a belief that multidisciplinary pods outperform siloed teams when customer trust is on the line.",
    icon: "🏁",
  },
  {
    title: "First National Roll-Out",
    year: "2023",
    description: "Delivered a nationwide public-sector programme that defined our playbooks for governance and scale.",
    icon: "🚀",
  },
  {
    title: "Global Pods",
    year: "2025",
    description: "Opened pods across North America, Asia, and the Middle East while keeping strategy anchored in the UK.",
    icon: "🌍",
  },
  {
    title: "Intelligent Delivery Toolkit",
    year: "2025",
    description:
      "Invested in AI assistants, reusable design systems, and observability accelerators that now power every engagement.",
    icon: "💻",
  },
  {
    title: "Industry Recognition",
    year: "2025",
    description:
      "Named among the most innovative product studios in the UK thanks to measurable outcomes and transparent delivery.",
    icon: "🏆",
  },
  {
    title: "Commitment Today",
    year: "Now",
    description:
      "Still independent and founder-led, we scale responsibly, staying embedded with clients until value is realised.",
    icon: "🌟",
  },
];

const StoryAboutUS = () => {
  const theme = useTheme();

  return (
    <Box component="section" id="journey" sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#ffffff" }}>
      <Container>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="overline" sx={{ letterSpacing: 3, color: theme.palette.secondary.main }}>
            Our Journey
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            Momentum built on curiosity, rigour, and trust
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 760,
              lineHeight: 1.8,
            }}
          >
            We grew from a small UK lab into a globally trusted studio by pairing design-led thinking with enterprise
            discipline. Each milestone reflects a new capability that now supports our partners.
          </Typography>
        </Stack>

        <Box
          sx={{
            position: "relative",
            mt: 8,
            "::before": {
              content: '""',
              position: "absolute",
              top: 24,
              bottom: 24,
              left: { xs: "16px", md: "50%" },
              width: 2,
              background: "linear-gradient(180deg, rgba(20,184,166,0.4), rgba(99,102,241,0.5))",
              transform: { md: "translateX(-1px)" },
            },
          }}
        >
          <Stack spacing={4}>
            {milestones.map((milestone, index) => {
              const isEven = index % 2 === 0;
              return (
                <Stack
                  key={milestone.title}
                  direction={{ xs: "column", md: isEven ? "row" : "row-reverse" }}
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={3}
                  sx={{ position: "relative" }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: { xs: 12, md: "50%" },
                      top: 20,
                      transform: { md: "translate(-50%, 0)" },
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.background.paper,
                      border: "5px solid #14b8a6",
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.1rem",
                    }}
                    aria-hidden
                  >
                    {milestone.icon}
                  </Box>
                  <Box flex={1} />
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      borderRadius: 5,
                      border: "1px solid rgba(15,23,42,0.08)",
                      background: isEven ? "#fef3c7" : "#ecfeff",
                      p: { xs: 3, md: 4 },
                      boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
                    }}
                  >
                    <Typography variant="overline" sx={{ letterSpacing: 2, color: theme.palette.secondary.main }}>
                      {milestone.year}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.8, color: "text.primary" }}>
                      {milestone.description}
                    </Typography>
                  </Paper>
                  <Box flex={1} />
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default StoryAboutUS;
